import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../services/api';
import { motion } from 'framer-motion';
import PasswordInput from '../components/PasswordInput';

const T = { teal: '#5EEAD4', tealDark: '#14B8A6' };

const GlassCard: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children, className = '',
}) => (
  <div
    className={`rounded-3xl border shadow-xl ${className}`}
    style={{
      background: 'rgba(255,255,255,0.75)',
      backdropFilter: 'blur(24px) saturate(1.2)',
      WebkitBackdropFilter: 'blur(24px) saturate(1.2)',
      borderColor: 'rgba(255,255,255,0.6)',
      boxShadow: '0 8px 32px rgba(148,163,184,0.12)',
    }}
  >
    {children}
  </div>
);

const VerifyEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [message, setMessage] = useState('');
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  // Proof of ownership handed back by /verify-email and required by
  // /set-password — that endpoint no longer accepts a bare email address.
  const [setPasswordToken, setSetPasswordToken] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link. Please check your email and try again.');
      return;
    }
    verifyEmail(token);
  }, [searchParams]);

  const verifyEmail = async (token: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/verify-email?token=${token}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (response.ok) {
        setStatus('success');
        setMessage(data.message || 'Email verified successfully!');
        setUserEmail(data.data?.email || '');
        setUserName(data.data?.firstName || '');
        setSetPasswordToken(data.setPasswordToken || '');
        setShowPasswordForm(true);
      } else {
        setStatus('error');
        setMessage(data.message || 'Verification failed. Please try again.');
      }
    } catch (error) {
      console.error('Verification error:', error);
      setStatus('error');
      setMessage('Something went wrong. Please try again later.');
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) { alert('Passwords do not match!'); return; }
    if (password.length < 8) { alert('Password must be at least 8 characters long!'); return; }
    try {
      const response = await fetch(`${API_BASE_URL}/api/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ setPasswordToken, password }),
      });
      const data = await response.json();
      if (response.ok) {
        alert('Password set successfully! You can now log in.');
        navigate('/login');
      } else {
        alert(data.message || 'Failed to set password. Please try again.');
      }
    } catch (error) {
      console.error('Password setting error:', error);
      alert('Something went wrong. Please try again later.');
    }
  };

  return (
    <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        <GlassCard className="p-8 sm:p-10">
          {/* Verifying */}
          {status === 'verifying' && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-6" style={{ borderColor: T.teal }} />
              <h2 className="text-xl font-bold text-slate-800 mb-2">Verifying Email...</h2>
              <p className="text-slate-500">Please wait while we verify your email address.</p>
            </div>
          )}

          {/* Success — set password */}
          {status === 'success' && showPasswordForm && (
            <div>
              <div className="text-center mb-8">
                <div className="text-5xl mb-3">✅</div>
                <h2 className="text-xl font-bold mb-1" style={{ color: T.tealDark }}>Email Verified!</h2>
                <p className="text-slate-500">Welcome to Karvlo, {userName}!</p>
                <p className="text-sm text-slate-400 mt-1">Set your password to complete setup.</p>
              </div>

              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1.5">Create Password</label>
                  <PasswordInput
                    id="password"
                    value={password}
                    onChange={setPassword}
                    placeholder="Enter your password"
                    required
                    showStrengthIndicator={true}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1.5">Confirm Password</label>
                  <PasswordInput
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={setConfirmPassword}
                    placeholder="Confirm your password"
                    required
                    error={confirmPassword && password !== confirmPassword ? 'Passwords do not match' : undefined}
                  />
                </div>
                <button
                  type="submit"
                  className="w-full py-3.5 bg-slate-800 text-white font-bold rounded-2xl shadow-lg shadow-slate-300/40 hover:bg-slate-700 hover:shadow-xl transition-all duration-300"
                >
                  Set Password & Continue
                </button>
              </form>
            </div>
          )}

          {/* Success — no password form */}
          {status === 'success' && !showPasswordForm && (
            <div className="text-center py-8">
              <div className="text-5xl mb-4">✅</div>
              <h2 className="text-xl font-bold mb-2" style={{ color: T.tealDark }}>Email Verified!</h2>
              <p className="text-slate-500">{message}</p>
            </div>
          )}

          {/* Error */}
          {status === 'error' && (
            <div className="text-center py-8">
              <div className="text-5xl mb-4">❌</div>
              <h2 className="text-xl font-bold text-rose-500 mb-2">Verification Failed</h2>
              <p className="text-slate-500 mb-6">{message}</p>
              <button
                onClick={() => navigate('/login')}
                className="px-8 py-3 bg-slate-800 text-white font-bold rounded-2xl shadow-lg hover:bg-slate-700 transition-all"
              >
                Go to Login
              </button>
            </div>
          )}

          {/* Footer */}
          <p className="text-center text-xs text-slate-400 mt-8">
            © {new Date().getFullYear()} Karvlo. Building the future of transportation in Hong Kong.
          </p>
        </GlassCard>
      </motion.div>
    </div>
  );
};

export default VerifyEmail;
